import { apiClient } from "@/lib/api";
import type {
  PaginatedStandardResponse,
  PaginationInfo,
  StandardResponse,
} from "@/types/api-response";
import { extractData, extractPaginatedData } from "@/types/api-response";
import type {
  DomainRegistrationAdminDto,
  DomainRegistrationFilters,
  DomainOperationDto,
  DomainOperationFilters,
  DomainAdminActionResultDto,
  ReconcileDomainRequest,
  RefundDomainRequest,
  CancelDomainRequest,
} from "@/types/domain";

/**
 * Admin Domain Service
 *
 * - `getAll` — read-only list of every domain registration (P0).
 * - `getOperations` + `retry`/`reconcile`/`refund`/`cancel` — the P1
 *   operations dashboard: triage and recover failed registrations.
 *
 * Routes:
 *   P0 list: GET /api/v1/domain-admin
 *   P1 ops:  /api/v1/domain-operations-admin (GET, POST /{id}/retry|reconcile|refund|cancel)
 */
export const adminDomainService = {
  /**
   * Get a paginated list of domain registrations across all tenants.
   *
   * @param filters - Status filter and pagination
   */
  async getAll(filters: DomainRegistrationFilters): Promise<{
    data: DomainRegistrationAdminDto[];
    pagination: PaginationInfo;
  }> {
    const params = new URLSearchParams();

    if (filters.status && filters.status !== "All") {
      params.append("status", filters.status);
    }
    params.append("page", filters.page.toString());
    params.append("pageSize", filters.pageSize.toString());

    const response = await apiClient.get<
      PaginatedStandardResponse<DomainRegistrationAdminDto>
    >(`/api/v1/domain-admin?${params.toString()}`);
    return extractPaginatedData(response.data);
  },

  /**
   * Get a paginated list of domain registrations that need admin
   * attention: RegistrationFailed, stuck Registering, TransferFailed,
   * or stuck TransferPending (P2).
   *
   * @param filters - Pagination, optional stuckMinutes threshold, and
   *                  optional stalePendingDays threshold for stale transfers
   */
  async getOperations(filters: DomainOperationFilters): Promise<{
    data: DomainOperationDto[];
    pagination: PaginationInfo;
  }> {
    const params = new URLSearchParams();

    params.append("page", filters.page.toString());
    params.append("pageSize", filters.pageSize.toString());
    if (filters.stuckMinutes !== undefined) {
      params.append("stuckMinutes", filters.stuckMinutes.toString());
    }
    if (filters.stalePendingDays !== undefined) {
      params.append("stalePendingDays", filters.stalePendingDays.toString());
    }

    const response = await apiClient.get<
      PaginatedStandardResponse<DomainOperationDto>
    >(`/api/v1/domain-operations-admin?${params.toString()}`);
    return extractPaginatedData(response.data);
  },

  /**
   * Retry provisioning a failed domain registration.
   *
   * Re-enqueues an admin-initiated ProvisionDomainJob.
   *
   * @param id - Domain registration ID (UUID)
   */
  async retry(id: string): Promise<DomainAdminActionResultDto> {
    const response = await apiClient.post<
      StandardResponse<DomainAdminActionResultDto>
    >(`/api/v1/domain-operations-admin/${id}/retry`);
    return extractData(response.data);
  },

  /**
   * Manually reconcile a domain that was registered out-of-band.
   *
   * Admin supplies the registrar reference and the confirmed expiry;
   * the backend moves the registration to Active.
   *
   * @param id - Domain registration ID (UUID)
   * @param data - Registrar reference and confirmed expiry datetime
   */
  async reconcile(
    id: string,
    data: ReconcileDomainRequest,
  ): Promise<DomainAdminActionResultDto> {
    const response = await apiClient.post<
      StandardResponse<DomainAdminActionResultDto>
    >(`/api/v1/domain-operations-admin/${id}/reconcile`, data);
    return extractData(response.data);
  },

  /**
   * Issue a partial refund for a failed domain order line.
   *
   * The backend creates a credit-note invoice and moves the
   * registration to Cancelled.
   *
   * @param id - Domain registration ID (UUID)
   * @param data - Admin-entered refund reason
   */
  async refund(
    id: string,
    data: RefundDomainRequest,
  ): Promise<DomainAdminActionResultDto> {
    const response = await apiClient.post<
      StandardResponse<DomainAdminActionResultDto>
    >(`/api/v1/domain-operations-admin/${id}/refund`, data);
    return extractData(response.data);
  },

  /**
   * Cancel a domain registration without a refund.
   *
   * @param id - Domain registration ID (UUID)
   * @param data - Admin-entered cancellation reason
   */
  async cancel(
    id: string,
    data: CancelDomainRequest,
  ): Promise<DomainAdminActionResultDto> {
    const response = await apiClient.post<
      StandardResponse<DomainAdminActionResultDto>
    >(`/api/v1/domain-operations-admin/${id}/cancel`, data);
    return extractData(response.data);
  },

  /**
   * Admin-initiated transfer status poll for a single TransferPending
   * domain (P2). Backend enqueues a one-shot TransferStatusPollJob.
   *
   * NOTE: Only meaningful for rows where status === "TransferPending".
   * The dashboard hides this action for other statuses.
   *
   * @param id - Domain registration ID (UUID)
   */
  async refreshTransfer(id: string): Promise<DomainAdminActionResultDto> {
    const response = await apiClient.post<
      StandardResponse<DomainAdminActionResultDto>
    >(`/api/v1/domain-operations-admin/${id}/refresh-transfer`);
    return extractData(response.data);
  },
};
